import clsx from "clsx";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import commonStyles from "@/styles/common.module.less";
import styles from "../ClientCUR/clientCUR.module.less";
import permissionStyle from "./userRoleCUR.module.less";
import PersonIcon from "@/assets/icons/PersonIcon";
import routes from "@/routes";
import ContentWrapper from "@/components/layouts/DashboardLayout/Content";
import FilterComponent from "../ClientCUR/components/FilterComponent";
import { Button, Col, Form, Input, notification, Row, Select, Tree, TreeProps } from "antd";
import message from "@/utils/message";
import { getError } from "@/utils/helpers";
import C from "@/utils/constants";
import { useGetRoleDetailQuery, useUpdateRoleMutation } from "@/rtk-query/commonQueries";

interface IClientCURModuleProps {
  typePage?: "create" | "view" | "edit";
}

function UserRoleCURModule({ typePage }: IClientCURModuleProps) {
  const params: any = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [permissonData, setPermissionData] = useState<any[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>(["0-1-0"]);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  // API
  const {
    data: roleInfo,
    isFetching: isFetchingRoleDetai,
  } = useGetRoleDetailQuery(Number(params?.id), {
    refetchOnMountOrArgChange: true,
  });
  const [updateRoleApi, { isLoading: isLoadingUpdateRole }] = useUpdateRoleMutation();

  const initPermissonTree = async () => {
    const keyChecked: string[] = [];
    const treePermissions = await C.PERMISSIONS_DETAIL.map((item, indexParent) => ({
      title: item.title,
      type: item.type,
      key: `0-${indexParent}`,
      children:
        (item?.permission as any[])?.map((child, index) => ({
          title: child.title,
          type: child.type,
          key: `0-${indexParent}-${index}`,
        })) ?? [],
    }));

    treePermissions.forEach((per) => {
      roleInfo?.permissions?.forEach((item) => {
        const fct = item.name.split(":");

        if (fct[0].toUpperCase() === "ALL") {
          per.children.forEach((child: any) => {
            keyChecked.push(child.key);
          });
        }

        if (fct[0].toUpperCase() !== "ALL" && per.type === fct[0].toUpperCase()) {
          if (per.children.some((child: any) => child.type === fct[fct.length - 1])) {
            const index = per.children.findIndex((child: any) => child.type === fct[fct.length - 1]);
            keyChecked.push(per.children[index].key);
          }
        }
      });
    });

    setPermissionData(treePermissions);
    setCheckedKeys(keyChecked);
  };

  const dropdownStyle = {
    background: "#dfe3ef",
    padding: 0,
    borderRadius: 0,
    boxShadow: "inset 4px 4px 3px rgba(0, 0, 0, 0.03)",
    fontSize: 12,
    fontWeight: 600,
  };

  const handleCheck: TreeProps["onCheck"] = (checkedKeysValue) => {
    setCheckedKeys(checkedKeysValue as React.Key[]);
  };

  const handleSubmit = async (valueForm: any) => {
    setIsSaveLoading(true);
    const permissionSelected: any[] = [];
    checkedKeys.forEach((checkedKey) => {
      if (checkedKey.toString().length >= 3) {
        permissonData.forEach((item) => {
          const filtered = item.children.filter((child: any) => child.key === checkedKey);
          if (filtered.length > 0) {
            permissionSelected.push(`${item.type.toLowerCase()}:${filtered[0].type}`.trim());
          }
        });
      }
    });
    try {
      const update = await updateRoleApi({
        id: params.id,
        body: {
          permissionsUpdate: permissionSelected,
          status: valueForm.status || 1,
        },
      }).unwrap();
      if (update) {
        setTimeout(() => {
          notification.success({
            key: "role_cur",
            message: message.SUCCESSFULLY,
          });
          setIsSaveLoading(false);

          history.push(routes.userRoleList.path);
        }, 1000);
      }
    } catch (err) {
      setIsSaveLoading(false);
      notification.error({
        key: "role_cur",
        message: getError(err),
      });
    }
  };

  const handleSetIntialValues = () => {
    if (!params.id || isFetchingRoleDetai) return;
    const initialValues = {
      userRole: roleInfo?.name || "",
      status: roleInfo?.status || 1,
    };

    form.setFields(
      Object.entries(initialValues).map(([name, value]) => ({
        name,
        value,
      }))
    );
  };

  const getCurrentType = () => {
    let currentType: any = {};
    switch (typePage) {
      case "view": {
        currentType = routes.viewRole;
        break;
      }
      case "edit": {
        currentType = routes.editRole;
        break;
      }
      default: {
        currentType = routes.createRole;
      }
    }
    return currentType;
  };

  useEffect(() => {
    handleSetIntialValues();
    initPermissonTree();
    return () => form.resetFields();
  }, [roleInfo, params]);

  return (
    <ContentWrapper
      title={getCurrentType()?.title}
      renderFilter={<FilterComponent title={getCurrentType()?.title} />}
      dataBreadCrumb={[
        {
          title: getCurrentType()?.title,
          url: getCurrentType()?.path,
          icon: <PersonIcon />,
        },
      ]}
    >
      <Form name="basic" labelCol={{ span: 6 }} wrapperCol={{ span: 24 }} onFinish={handleSubmit} autoComplete="off" form={form}>
        <div className={styles.infoGeneral}>
          <Row gutter={[50, 16]}>
            <Col span={12}>
              <Form.Item label={<span className={commonStyles.label}>User role</span>} name="userRole">
                <Input
                  className={clsx(commonStyles.textField)}
                  style={{
                    height: 38,
                  }}
                  size="large"
                  readOnly
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span className={commonStyles.label}>Status</span>}
                name="status"
                rules={[
                  {
                    required: true,
                    message: message.ERROR_NOT_BLANK("Status"),
                  },
                ]}
              >
                <Select
                  className={commonStyles.select}
                  size="large"
                  dropdownStyle={dropdownStyle}
                  popupClassName="customDropdownSelectAntd"
                  placeholder=""
                  optionFilterProp="children"
                  allowClear
                  getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement}
                  disabled={typePage === "view"}
                >
                  {Object.entries(C.USER_STATUS)
                    .slice(Object.keys(C.USER_STATUS).length / 2)
                    .map(([key, value], index) => (
                      <Select.Option value={value} key={index}>
                        {key}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className="">
          <Tree
            className={permissionStyle.custommizeTreePermission}
            checkable
            disabled={typePage === "view"}
            selectable={false}
            checkedKeys={checkedKeys}
            onCheck={handleCheck}
            treeData={permissonData}
            rootClassName={permissionStyle.custommizeTreePermission}
          />
        </div>

        {typePage !== "view" && (
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "flex-end",
              gap: 14,
            }}
          >
            <Button
              type="primary"
              size="large"
              onClick={() => history.goBack()}
              style={{
                background: "#8C8888",
              }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              size="large"
              style={{
                width: 180,
                fontWeight: 600,
              }}
              loading={isSaveLoading}
              disabled={isLoadingUpdateRole || isFetchingRoleDetai}
            >
              Save
            </Button>
          </div>
        )}
      </Form>
    </ContentWrapper>
  );
}

export default UserRoleCURModule;
